import apiClient from "@/api/apiClient";
import { type ArgsCreateCustomerDeployment, type Deployment } from "@/supabase.types";
import {
  createPaginatedSupabaseQuery,
  spawnCreateApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";

import type { PaginatedDeployment, PaginatedDeploymentWithRoles } from "@/api/resources/deployments/types";
import type { QueryPaginationFilters } from "@/utils/pagination";

const table = "deployments";
const query =
  "*, settings:deployment_settings(*), devices(id), users:users_to_deployments(user_id), public_portals(id)";
const queryWithRoles = "*, workspace:workspaces!deployments_workspace_id_fkey(*), users_to_deployments(*)";

export const apiConfigDeployments: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "address_1", "address_2", "city", "country", "zipcode"],
  sortables: ["name", "created_at", "updated_at"],
};

export const apiConfigDeploymentsWithRoles: ApiResourceConfig<typeof table, typeof queryWithRoles> = {
  table,
  query: queryWithRoles,
  searchables: ["name", "address_1", "address_2", "city", "country", "zipcode"],
  sortables: ["name", "created_at", "updated_at"],
};

export async function getDeployments(): Promise<PaginatedDeployment[]> {
  const { data, error } = await apiClient
    .from(table)
    .select(query)
    .is("deleted_at", null)
    .is("devices.deleted_at", null);
  if (error) throw error;
  return data;
}

export async function getDeploymentsWithRoles(): Promise<PaginatedDeploymentWithRoles[]> {
  const { data, error } = await apiClient.from(table).select(queryWithRoles).is("deleted_at", null);
  if (error) throw error;
  return data;
}

export const getDeployment = async (match: Partial<Deployment>): Promise<PaginatedDeployment | null> => {
  const { data, error } = await apiClient
    .from(table)
    .select(query)
    .match(match)
    .is("deleted_at", null)
    .is("devices.deleted_at", null)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const createDeployment = spawnCreateApiFunction({ table, query });
export const updateDeployment = spawnUpdateApiFunction({ table, query });
export const deleteDeployment = async ({ id }: Pick<Deployment, "id">) => {
  const { error, ...rest } = await apiClient.rpc("delete_deployment", { _deployment_id: id });
  if (error) throw error;

  return { error, ...rest };
};

export async function getPaginatedDeployments({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigDeployments.searchables,
      sortables: apiConfigDeployments.sortables,
      filters: [
        ["deleted_at", "is", null], // filter out soft-deleted deployments
        ["devices.deleted_at", "is", null], // filter out soft-deleted deployments
      ],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });

  if (error) throw error;
  return { data, count };
}

export const createCustomerDeployment = async (args: ArgsCreateCustomerDeployment): Promise<Deployment> => {
  const { error, data } = await apiClient.rpc("create_customer_deployment", args);
  if (error) throw error;
  return data;
};
